import React, { useState, useRef, useEffect, useContext } from 'react';
import FeatherIcon from 'feather-icons-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ExploreNav from '../metrics/nav';
import ExportWizard from './ExportWizard';
import GoogleSheetsConfig from './GoogleSheetsConfig';
import { ViewContext, useAPI } from 'components/lib';
const axios = require('axios');

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}


export function Export() {
  const [exportsList, setExportsList] = useState([]);
  const [currentExportIndex, setCurrentExportIndex] = useState(0);
  const [hoveredExportIndex, setHoveredExportIndex] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const googleSheetsConfigRef = useRef(null);
  
  const context = useContext(ViewContext);
  const initialTemplates = [
    {
      name: "Connect GL to Google Sheets",
      value: "gl_to_sheets",
      icon: "database",
      description: "Connect the trial balance and P&L data directly to Google Sheets",
      data: {
        showGoogleSheets: true
      }
    },
    // {
    //   name: "Create Your Own Export",
    //   value: "custom_export",
    //   icon: "file-text",
    //   description: "Customize your export using the Export Wizard",
    //   data: {
    //     showFullWizard: true
    //   }
    // }
  ];

  const getTemplateById = (id) => initialTemplates.find(template => template.id === id);

  const exportsAPI = useAPI('/api/document/list/export');

  useEffect(() => {
    
    if (exportsAPI?.data) {
      console.log('Fetched exports:', exportsAPI.data);
      setExportsList(exportsAPI.data);
      setCurrentExportIndex(0);
    }
  }, [exportsAPI.data]);

  useEffect(() => {
    if (exportsList.length === 0 || currentExportIndex === null) {
        setShowTemplates(true);
    }
  }, [exportsList, currentExportIndex]);

  const addNewExport = () => {
    setShowTemplates(true);
  };

  const selectTemplate = (template) => {
    const newExportIndex = exportsList.length;
    setExportsList([...exportsList, { id: template.id, name: `Export ${newExportIndex + 1}` }]);
    setCurrentExportIndex(newExportIndex);
    setShowTemplates(false);

    if (template.showFullWizard) {
        setExportsList((prevExports) => {
            const updatedExports = [...prevExports];
            return updatedExports;
        });
    }
  };

  const handleExportDragEnd = (result) => {
    if (!result.destination) return;
    const updatedExports = reorder(exportsList, result.source.index, result.destination.index);
    setExportsList(updatedExports);
    setCurrentExportIndex(result.destination.index);
  };

  const handleDeleteExport = async (index) => {
    if (window.confirm(`Are you sure you want to delete this export?`)) {
      const exportId = exportsList[index]?.id;
      if (exportId) {
        try {
          await axios.delete(`/api/document/${exportId}`);
        } catch (error) {
          console.error('Error deleting export:', error);
          context.notification.show('Failed to delete export. Please try again.', 'error', true, 'toast', 'alert-circle');
          return;
        }
      }

      const updatedExports = exportsList.filter((_, i) => i !== index);
      setExportsList(updatedExports);
      if (index === currentExportIndex) {
        setCurrentExportIndex(updatedExports.length === 0 ? 0 : Math.min(currentExportIndex, updatedExports.length - 1));
      } else if (index === exportsList.length - 1) {
        setCurrentExportIndex(0);
      } else {
        setCurrentExportIndex(prevIndex => Math.min(prevIndex, updatedExports.length - 1));
      }
    }
  };

  const goToExport = (index) => {
    if (index >= 0 && index < exportsList.length) {
      setCurrentExportIndex(index);
      setShowTemplates(false);
    }
  };

  const toggleEditingTitle = () => {
    setEditingTitle(!editingTitle);
  };

  const handleTitleChange = (e) => {
    const updatedExports = [...exportsList];
    updatedExports[currentExportIndex].name = e.target.value;
    setExportsList(updatedExports);
  };

  const handleTitleBlur = () => {
    const updatedExports = [...exportsList];
    if (!updatedExports[currentExportIndex].name.trim()) {
      updatedExports[currentExportIndex].name = 'Export';
    }
    setExportsList(updatedExports);
    toggleEditingTitle();
  };

  const handleTemplateClick = (template) => {
    selectTemplate(template);
  };


  const handleSaveGoogleSheetsConfig = async () => {
    const isValid = googleSheetsConfigRef.current?.handleValidation();
    if (isValid) {
        const updatedConfig = {
            ...googleSheetsConfigRef.current?.getConfig(),
            showGoogleSheets: true
        };
        console.log('Updated Google Sheets config:', updatedConfig);

        // Update the current export's data
        setExportsList((prevExports) => {
            const updatedExports = [...prevExports];
            updatedExports[currentExportIndex].data = updatedConfig;
            return updatedExports;
        });

        try {
            let exportId = exportsList[currentExportIndex]?.id;
            if (!exportId) {
                // Create a new export if no id exists
                const createResponse = await axios.post('/api/document', {
                    type: 'export',
                    name: exportsList[currentExportIndex]?.name || 'Untitled Export',
                    data: updatedConfig,
                });
                exportId = createResponse.data.id;

                setExportsList((prevExports) => {
                    const updatedExports = [...prevExports];
                    updatedExports[currentExportIndex].id = exportId;
                    return updatedExports;
                });
            } else {
                // Update the existing export
                await axios.put(`/api/document/${exportId}`, {
                    type: 'export',
                    name: exportsList[currentExportIndex]?.name || 'Untitled Export',
                    data: updatedConfig,
                });
            }

            context.notification.show('Your export configuration has been updated.', 'success', true, 'toast', 'check'); 
        } catch (error) {
            console.error('Error updating export:', error);
            context.notification.show('Failed to update export. Please try again.', 'error', true, 'toast', 'alert-circle');
        }
    }
};

  const handleRefreshGoogleSheetsConfig = async () => {

    try {
      let exportId = exportsList[currentExportIndex]?.id;
      
      await axios.post(`/api/document/export/process/${exportId}`, {
      });
    // Placeholder: implement refresh logic here
    context.notification.show('Process kicked off.', 'info', true, 'toast', 'refresh-cw');
    } catch (error) {
        console.error('Error updating export:', error);
        context.notification.show('Failed to kick off this export. Please try again.', 'error', true, 'toast', 'alert-circle');
    }
  };

  return (
    <>
      <ExploreNav />
      <div style={{ display: 'flex', height: 'calc(100vh - 40px)', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flex: 1 }}>
          <div style={{ width: '200px', backgroundColor: '#f4f4f4', padding: '10px', boxShadow: '2px 0 5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Exports</h3>
              <button onClick={addNewExport} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FeatherIcon icon="plus" size={20} />
              </button>
            </div>
            <DragDropContext onDragEnd={handleExportDragEnd}>
              <Droppable droppableId="droppable-exports">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <ul style={{ listStyleType: 'none', padding: 0 }}>
                      {exportsList.map((exp, index) => (
                        <Draggable key={index} draggableId={`export-${index}`} index={index}>
                          {(provided) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: '10px',
                                cursor: 'pointer',
                                padding: '5px',
                                backgroundColor: currentExportIndex === index ? '#ddd' : 'transparent',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                height: '24px',
                              }}
                              onClick={() => goToExport(index)}
                              onMouseEnter={() => setHoveredExportIndex(index)}
                              onMouseLeave={() => setHoveredExportIndex(null)}
                            >
                              {exp.name.length > 18 ? `${exp.name.substring(0, 18)}...` : exp.name}
                              {hoveredExportIndex === index && (
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteExport(index); }} style={{ background: 'none', border: 'none', cursor: 'pointer', paddingTop: '6px', color: 'inherit' }}>
                                  <FeatherIcon icon="trash-2" size={16} />
                                </button>
                              )}
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          {exportsList.length > 0 && !showTemplates &&
          <div style={{ flex: 1, padding: '20px', position: 'relative', backgroundColor: '#fff', borderRadius: '5px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {editingTitle ? (
                <input
                  type="text"
                  value={exportsList[currentExportIndex].name}
                  onChange={handleTitleChange}
                  onBlur={handleTitleBlur}
                  autoFocus
                  style={{ fontSize: '18px', width: '70%' }}
                />
              ) : (
                <h2 onClick={toggleEditingTitle} style={{ cursor: 'pointer', margin: '0px', width: '70%' }}>
                  {exportsList[currentExportIndex].name}
                </h2>
              )}
            </div>
            <div style={{paddingTop: '20px'}}>
              {(exportsList[currentExportIndex]?.data?.showGoogleSheets || getTemplateById(exportsList[currentExportIndex].id)?.data?.showGoogleSheets) ? (
                <div>
                  <GoogleSheetsConfig
                    ref={googleSheetsConfigRef}
                    googleSheetsConfig={exportsList[currentExportIndex]?.data}
                    onValidate={() => {}}
                  />
                  <button 
                    onClick={handleSaveGoogleSheetsConfig} 
                    style={{ 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      padding: '10px 20px', 
                      cursor: 'pointer', 
                      position: 'absolute', 
                      top: '20px', 
                      right: '20px' 
                    }}
                  >
                    Save
                  </button>
                  {exportsList[currentExportIndex]?.id && (
                    <button
                      onClick={handleRefreshGoogleSheetsConfig}
                      style={{
                        backgroundColor: '#218838',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '10px 16px',
                        cursor: 'pointer',
                        position: 'absolute',
                        top: '20px',
                        right: '110px'
                      }}
                      title="Refresh Google Sheets Config"
                    >
                      <FeatherIcon icon="refresh-cw" size={16} />
                    </button>
                  )}
                </div>
              ) : (
                <ExportWizard />
              )}
            </div>
            
          </div>
          }
          {(showTemplates || exportsList.length === 0) &&
          <div style={{ flex: 1, padding: '20px', display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
            {initialTemplates.map((template, index) => (
              <div
                key={template.value}
                onClick={() => handleTemplateClick(template)}
                style={{ cursor: 'pointer', width: '200px', height: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd', borderRadius: '10px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '60px' }}>
                  <FeatherIcon icon={template.icon} size={40} />
                </div>
                <h3>{template.name}</h3>
                <p style={{ textAlign: 'center' }}>{template.description}</p>
              </div>
            ))}
          </div>
          }
        </div>
      </div>
    </>
  );
}